import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { RefreshCw, History, Flame, Award, Target, Star, Zap, Trophy } from 'lucide-react';
import axios from 'axios';
import {
  fadeInUp,
  GlassmorphicCard,
  SectionTitle,
  BodyText,
  SecondaryText,
} from '../styles/sharedStyles';

function OralB({ apiBaseUrl, token }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${apiBaseUrl}/api/oralb/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load Oral-B status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  const fetchGamification = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${apiBaseUrl}/api/oralb/gamification`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGamification(res.data);
    } catch (err) {
      console.error('Failed to load gamification data:', err);
    }
  }, [apiBaseUrl, token]);

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${apiBaseUrl}/api/oralb/history?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data?.history || []);
    } catch (err) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchStatus();
    fetchGamification();
    fetchHistory();
    const interval = setInterval(() => {
      fetchStatus();
      fetchGamification();
      fetchHistory();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchGamification, fetchHistory]);

  const lastSession = status?.last_session || {};
  const settings = status?.settings || {};
  const sensors = status?.sensors || {};
  const challenges = status?.challenges || {};
  const weeklyDays = challenges.weekly_days || [0, 0, 0, 0, 0, 0, 0];
  const monthlyDays = challenges.monthly_days || [];
  // Week = Mon–Sun (backend index 0–6); each day has a distinct colour
  const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const WEEKDAY_COLORS = ['#00d4ff', '#9b59b6', '#2ecc71', '#f1c40f', '#e74c3c', '#3498db', '#e67e22'];

  // Gamification values
  const level = gamification?.level || 1;
  const xp = gamification?.xp || 0;
  const xpProgress = gamification?.xp_progress || 0;
  const xpNeeded = gamification?.xp_needed || 100;
  const currentStreak = gamification?.current_streak || 0;
  const bestStreak = gamification?.best_streak || 0;
  const achievements = gamification?.achievements || [];
  const dailyGoals = gamification?.daily_goals || {};
  const stats = gamification?.stats || {};
  const motivationalMessage = gamification?.motivational_message || '';

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ animation: `${fadeInUp} 0.4s ease-out` }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/oralb.png"
            alt="Oral-B"
            sx={{ width: 36, height: 36, objectFit: 'contain' }}
          />
          <SectionTitle sx={{ mb: 0 }}>Toothbrush Dashboard</SectionTitle>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshCw size={18} />}
          onClick={() => {
            fetchStatus();
            fetchGamification();
            fetchHistory();
          }}
          disabled={loading}
          sx={{
            borderColor: 'rgba(0, 212, 255, 0.4)',
            color: '#00d4ff',
            '&:hover': {
              borderColor: '#00d4ff',
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Hero Card - Level, XP, Streak */}
      <GlassmorphicCard
        sx={{
          p: isMobile ? 2 : 3,
          mb: 3,
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(155, 89, 182, 0.1) 100%)',
          border: '1px solid rgba(0, 212, 255, 0.3)',
        }}
      >
        <Grid container spacing={isMobile ? 2 : 3} alignItems="center">
          {/* Level Badge */}
          <Grid item xs={4} sm={3} md={2}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: isMobile ? 56 : 80,
                  height: isMobile ? 56 : 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 20px rgba(155, 89, 182, 0.5)',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 20px rgba(155, 89, 182, 0.5)' },
                    '50%': { transform: 'scale(1.05)', boxShadow: '0 0 30px rgba(155, 89, 182, 0.8)' },
                  },
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
                  {level}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, fontSize: isMobile ? '0.65rem' : 'inherit' }}>
                LEVEL
              </Typography>
            </Box>
          </Grid>

          {/* XP Progress */}
          <Grid item xs={8} sm={5} md={6}>
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Zap size={isMobile ? 14 : 18} color="#ffd700" />
                  <BodyText sx={{ fontWeight: 600, color: '#ffd700', fontSize: isMobile ? '0.85rem' : 'inherit' }}>
                    {xp.toLocaleString()} XP
                  </BodyText>
                </Box>
                <SecondaryText sx={{ fontSize: isMobile ? '0.65rem' : 'inherit' }}>
                  {xpProgress}/{xpNeeded}
                </SecondaryText>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((xpProgress / xpNeeded) * 100, 100)}
                sx={{
                  height: isMobile ? 8 : 12,
                  borderRadius: 6,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 6,
                    background: 'linear-gradient(90deg, #ffd700 0%, #ffed4a 100%)',
                    boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
                  },
                }}
              />
            </Box>
          </Grid>

          {/* Streak */}
          <Grid item xs={12} sm={4} md={4}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: isMobile ? 1.5 : 2,
                gap: isMobile ? 2 : 0,
                borderRadius: 2,
                bgcolor: currentStreak > 0 ? 'rgba(255, 107, 53, 0.15)' : 'rgba(255,255,255,0.05)',
                border: currentStreak > 0 ? '1px solid rgba(255, 107, 53, 0.3)' : '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Flame
                  size={isMobile ? 22 : 28}
                  color={currentStreak > 0 ? '#ff6b35' : 'rgba(255,255,255,0.3)'}
                  fill={currentStreak > 0 ? '#ff6b35' : 'none'}
                  style={currentStreak > 0 ? { animation: 'flame 0.5s ease-in-out infinite' } : {}}
                />
                <Typography sx={{ color: currentStreak > 0 ? '#ff6b35' : 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
                  {currentStreak}
                </Typography>
              </Box>
              <Box sx={{ textAlign: isMobile ? 'left' : 'center' }}>
                <Typography variant="caption" sx={{ color: currentStreak > 0 ? '#ff6b35' : 'rgba(255,255,255,0.5)' }}>
                  Day Streak {currentStreak >= 7 && '🔥'}
                </Typography>
                <SecondaryText sx={{ fontSize: '0.7rem', display: 'block' }}>
                  Best: {bestStreak} days
                </SecondaryText>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Motivational Message */}
        {motivationalMessage && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(46, 204, 113, 0.1)',
              border: '1px solid rgba(46, 204, 113, 0.3)',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: '#2ecc71', fontWeight: 500 }}>
              {motivationalMessage}
            </Typography>
          </Box>
        )}
      </GlassmorphicCard>

      {/* Daily Quests */}
      <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <GlassmorphicCard
            sx={{
              p: isMobile ? 2 : 2.5,
              height: '100%',
              border: '1px solid rgba(0, 212, 255, 0.15)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Target size={isMobile ? 16 : 20} color="#00d4ff" />
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 600, fontSize: isMobile ? '0.9rem' : 'inherit' }}>
                Today's Quests
              </Typography>
            </Box>

            {/* Morning Quest */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                mb: 1,
                borderRadius: 2,
                bgcolor: dailyGoals.morning?.status === 'completed' ? 'rgba(46, 204, 113, 0.15)' :
                         dailyGoals.morning?.status === 'missed' ? 'rgba(231, 76, 60, 0.15)' :
                         'rgba(255,255,255,0.05)',
                border: '1px solid',
                borderColor: dailyGoals.morning?.status === 'completed' ? 'rgba(46, 204, 113, 0.3)' :
                             dailyGoals.morning?.status === 'missed' ? 'rgba(231, 76, 60, 0.3)' :
                             'rgba(255,255,255,0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: dailyGoals.morning?.done ? '#2ecc71' : 'rgba(255,255,255,0.1)',
                  }}
                >
                  {dailyGoals.morning?.done ? (
                    <Typography sx={{ color: '#fff', fontWeight: 700 }}>✓</Typography>
                  ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>🌅</Typography>
                  )}
                </Box>
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '0.9rem' }}>
                    Morning Brush
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                    {dailyGoals.morning?.window || '8:00 - 10:00'}
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                label={dailyGoals.morning?.status === 'completed' ? 'Done!' :
                       dailyGoals.morning?.status === 'missed' ? 'Missed' :
                       dailyGoals.morning?.status === 'in_progress' ? 'In Progress' : 'Pending'}
                sx={{
                  bgcolor: dailyGoals.morning?.status === 'completed' ? '#2ecc71' :
                           dailyGoals.morning?.status === 'missed' ? '#e74c3c' :
                           'rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            </Box>

            {/* Evening Quest */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                borderRadius: 2,
                bgcolor: dailyGoals.evening?.status === 'completed' ? 'rgba(46, 204, 113, 0.15)' :
                         dailyGoals.evening?.status === 'missed' ? 'rgba(231, 76, 60, 0.15)' :
                         'rgba(255,255,255,0.05)',
                border: '1px solid',
                borderColor: dailyGoals.evening?.status === 'completed' ? 'rgba(46, 204, 113, 0.3)' :
                             dailyGoals.evening?.status === 'missed' ? 'rgba(231, 76, 60, 0.3)' :
                             'rgba(255,255,255,0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: dailyGoals.evening?.done ? '#2ecc71' : 'rgba(255,255,255,0.1)',
                  }}
                >
                  {dailyGoals.evening?.done ? (
                    <Typography sx={{ color: '#fff', fontWeight: 700 }}>✓</Typography>
                  ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>🌙</Typography>
                  )}
                </Box>
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '0.9rem' }}>
                    Evening Brush
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                    {dailyGoals.evening?.window || '20:00 - 22:00'}
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                label={dailyGoals.evening?.status === 'completed' ? 'Done!' :
                       dailyGoals.evening?.status === 'missed' ? 'Missed' :
                       dailyGoals.evening?.status === 'in_progress' ? 'In Progress' : 'Pending'}
                sx={{
                  bgcolor: dailyGoals.evening?.status === 'completed' ? '#2ecc71' :
                           dailyGoals.evening?.status === 'missed' ? '#e74c3c' :
                           'rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            </Box>
          </GlassmorphicCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <GlassmorphicCard
            sx={{
              p: isMobile ? 2 : 2.5,
              height: '100%',
              border: '1px solid rgba(0, 212, 255, 0.15)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Trophy size={isMobile ? 16 : 20} color="#ffd700" />
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 600, fontSize: isMobile ? '0.9rem' : 'inherit' }}>
                Personal Records
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)' }}>
                  <Typography variant="h5" sx={{ color: '#ffd700', fontWeight: 700 }}>
                    {stats.total_sessions || 0}
                  </Typography>
                  <SecondaryText>Total Brushes</SecondaryText>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)' }}>
                  <Typography variant="h5" sx={{ color: '#ff6b35', fontWeight: 700 }}>
                    {bestStreak}
                  </Typography>
                  <SecondaryText>Best Streak</SecondaryText>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)' }}>
                  <Typography variant="h5" sx={{ color: '#00d4ff', fontWeight: 700 }}>
                    {stats.best_duration ? `${Math.floor(stats.best_duration / 60)}:${String(stats.best_duration % 60).padStart(2, '0')}` : '0:00'}
                  </Typography>
                  <SecondaryText>Best Session</SecondaryText>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)' }}>
                  <Typography variant="h5" sx={{ color: '#2ecc71', fontWeight: 700 }}>
                    {stats.average_duration || 0}s
                  </Typography>
                  <SecondaryText>Avg Duration</SecondaryText>
                </Box>
              </Grid>
            </Grid>
          </GlassmorphicCard>
        </Grid>
      </Grid>

      {/* Achievements */}
      <GlassmorphicCard sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Award size={20} color="#ffd700" />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
            Achievements
          </Typography>
          <Chip
            size="small"
            label={`${gamification?.unlocked_count || 0}/${gamification?.total_achievements || 0}`}
            sx={{ ml: 'auto', bgcolor: 'rgba(255, 215, 0, 0.2)', color: '#ffd700' }}
          />
        </Box>
        <Grid container spacing={1.5}>
          {achievements.map((ach) => (
            <Grid item xs={6} sm={4} md={3} key={ach.id}>
              <Box
                sx={{
                  p: isMobile ? 1 : 1.5,
                  borderRadius: 2,
                  textAlign: 'center',
                  bgcolor: ach.unlocked ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255,255,255,0.03)',
                  border: '1px solid',
                  borderColor: ach.unlocked ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255,255,255,0.05)',
                  opacity: ach.unlocked ? 1 : 0.5,
                  transition: 'all 0.3s ease',
                  '&:hover': ach.unlocked ? {
                    transform: 'scale(1.02)',
                    boxShadow: '0 0 15px rgba(255, 215, 0, 0.3)',
                  } : {},
                }}
              >
                <Typography sx={{ mb: 0.5, fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
                  {ach.icon}
                </Typography>
                <Typography
                  sx={{
                    color: ach.unlocked ? '#ffd700' : 'rgba(255,255,255,0.4)',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                >
                  {ach.name}
                </Typography>
                <SecondaryText sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                  {ach.description}
                </SecondaryText>
              </Box>
            </Grid>
          ))}
        </Grid>
      </GlassmorphicCard>

      {/* Weekly challenge: Mon–Sun, resets at end of Sunday */}
      <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <GlassmorphicCard
            sx={{
              p: isMobile ? 2 : 2.5,
              height: '100%',
              border: '1px solid rgba(0, 212, 255, 0.15)',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5, fontWeight: 600 }}>
              📅 Weekly challenge (Mon–Sun)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 0.75 : 1.5, alignItems: 'flex-end', mb: 1 }}>
              {weeklyDays.map((count, i) => {
                const dayColor = WEEKDAY_COLORS[i] || '#00d4ff';
                const isFull = count >= 2;
                const isHalf = count === 1;
                return (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: isMobile ? 32 : 36,
                    }}
                  >
                    <Box
                      title={count >= 2 ? '2 brushes' : count === 1 ? '1 brush' : 'No brush'}
                      sx={{
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: isFull || isHalf ? `${dayColor}99` : 'rgba(255,255,255,0.2)',
                        background: isFull
                          ? `linear-gradient(135deg, ${dayColor} 0%, ${dayColor}99 100%)`
                          : isHalf
                            ? `linear-gradient(90deg, ${dayColor} 50%, transparent 50%)`
                            : 'transparent',
                        boxShadow: isFull || isHalf ? `0 0 10px ${dayColor}66` : 'none',
                        transition: 'all 0.3s ease',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.5,
                        color: 'rgba(255,255,255,0.8)',
                        fontWeight: 600,
                        fontSize: isMobile ? '0.65rem' : '0.7rem',
                      }}
                    >
                      {WEEKDAY_NAMES[i]}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <BodyText sx={{ fontWeight: 600, color: '#00d4ff' }}>
              {weeklyDays.filter((c) => c > 0).length} days with brushes · {weeklyDays.reduce((a, b) => a + b, 0)} total
            </BodyText>
          </GlassmorphicCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <GlassmorphicCard
            sx={{
              p: isMobile ? 2 : 2.5,
              height: '100%',
              border: '1px solid rgba(0, 212, 255, 0.15)',
              '& .point-m': {
                width: 14,
                height: 14,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.2)',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
              },
              '& .point-m.half': {
                background: 'linear-gradient(90deg, #00d4ff 50%, transparent 50%)',
                borderColor: 'rgba(0, 212, 255, 0.5)',
                boxShadow: '0 0 6px rgba(0, 212, 255, 0.4)',
                animation: 'oralbPointGlowM 2s ease-in-out infinite',
              },
              '& .point-m.full': {
                background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                borderColor: 'rgba(0, 212, 255, 0.6)',
                boxShadow: '0 0 8px rgba(0, 212, 255, 0.4)',
                animation: 'oralbPointGlowM 2s ease-in-out infinite',
              },
              '@keyframes oralbPointGlowM': {
                '0%, 100%': { boxShadow: '0 0 8px rgba(0, 212, 255, 0.4)' },
                '50%': { boxShadow: '0 0 14px rgba(0, 212, 255, 0.7)' },
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5, fontWeight: 600 }}>
              🗓️ Monthly challenge ({monthlyDays.length} days)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, alignItems: 'center', mb: 1 }}>
              {monthlyDays.map((count, i) => (
                <Box
                  key={i}
                  className={`point-m ${count >= 2 ? 'full' : count === 1 ? 'half' : ''}`}
                  title={`Day ${i + 1}: ${count >= 2 ? '2 brushes' : count === 1 ? '1 brush' : 'No brush'}`}
                />
              ))}
            </Box>
            <BodyText sx={{ fontWeight: 600, color: '#00d4ff' }}>
              {monthlyDays.filter((c) => c > 0).length} days with brushes · {monthlyDays.reduce((a, b) => a + b, 0)} total
            </BodyText>
          </GlassmorphicCard>
        </Grid>
      </Grid>

      {/* Last Session Summary */}
      <GlassmorphicCard sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2, fontSize: isMobile ? '1rem' : '1.25rem' }}>
          Last Session Summary
        </Typography>
        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={6} sm={3}>
            <SecondaryText>Duration</SecondaryText>
            <BodyText sx={{ fontWeight: 600 }}>
              {lastSession.duration != null ? `${lastSession.duration} s` : 'N/A'}
            </BodyText>
          </Grid>
          <Grid item xs={6} sm={3}>
            <SecondaryText>Mode</SecondaryText>
            <BodyText sx={{ fontWeight: 600 }}>
              {lastSession.mode ? String(lastSession.mode).replace(/_/g, ' ') : 'N/A'}
            </BodyText>
          </Grid>
          <Grid item xs={6} sm={3}>
            <SecondaryText>Pressure Warnings</SecondaryText>
            <BodyText sx={{ fontWeight: 600 }}>{lastSession.pressure_warnings ?? 'N/A'}</BodyText>
          </Grid>
          <Grid item xs={6} sm={3}>
            <SecondaryText>Battery</SecondaryText>
            <BodyText sx={{ fontWeight: 600 }}>
              {lastSession.battery != null ? `${lastSession.battery}%` : 'N/A'}
            </BodyText>
          </Grid>
        </Grid>
      </GlassmorphicCard>

      {/* Live Sensor Data */}
      {Object.keys(sensors).length > 0 && (
        <GlassmorphicCard sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2, fontSize: isMobile ? '1rem' : '1.25rem' }}>
            Live Sensor Data
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(sensors).map(([name, value]) => {
              let display = String(value).replace(/_/g, ' ');
              if (name === 'Battery') display += ' %';
              else if (name === 'Brushing Time') display += ' s';
              else if (name === 'Signal Strength') display += ' dBm';
              let color = 'rgba(255,255,255,0.9)';
              if (name === 'Battery') {
                const n = parseInt(value, 10);
                if (!isNaN(n)) {
                  if (n > 40) color = '#2ecc71';
                  else if (n >= 20) color = '#f1c40f';
                  else color = '#e74c3c';
                }
              }
              if (name === 'Pressure' && value) {
                const v = String(value).toLowerCase();
                if (v === 'normal') color = '#2ecc71';
                else if (v === 'low' || v === 'high') color = '#e74c3c';
              }
              return (
                <Grid item xs={6} sm={3} key={name}>
                  <Box
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <SecondaryText sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem', textTransform: 'uppercase' }}>
                      {name}
                    </SecondaryText>
                    <Typography sx={{ color, fontWeight: 600, fontSize: isMobile ? '0.9rem' : '1.1rem' }}>
                      {display || '—'}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </GlassmorphicCard>
      )}

      {/* Brushing History */}
      <GlassmorphicCard sx={{ p: isMobile ? 2 : 3 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: isMobile ? '1rem' : '1.25rem' }}>
          <History size={isMobile ? 16 : 20} /> Brushing History
        </Typography>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#00d4ff' }} />
          </Box>
        ) : history.length === 0 ? (
          <SecondaryText>No history yet. Data is read from the backend (oralb/ or data/oralb/).</SecondaryText>
        ) : (
          <Box sx={{ overflowX: 'auto', mx: isMobile ? -2 : 0, px: isMobile ? 2 : 0 }}>
            <TableContainer
              sx={{
                maxHeight: 400,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.06)',
                bgcolor: 'rgba(255,255,255,0.02)',
                minWidth: isMobile ? 500 : 'auto',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>Date</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>Time</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>Dur (s)</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>Mode</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>Pressure</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((entry, i) => {
                    const ts = entry.timestamp ? new Date(entry.timestamp) : null;
                    return (
                      <TableRow key={i} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>
                          {ts ? ts.toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>
                          {ts ? ts.toLocaleTimeString() : '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>{entry.duration ?? '—'}</TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>
                          {entry.mode ? String(entry.mode).replace(/_/g, ' ') : '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.7rem' : 'inherit', py: 1, px: isMobile ? 1 : 2 }}>
                          {entry.pressure_warnings ?? '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </GlassmorphicCard>

      <Box sx={{ mt: 2 }}>
        <SecondaryText sx={{ fontSize: '0.8rem' }}>
          Sessions and history from backend. Live sensors from Home Assistant. Reminder emails at the end of morning/evening windows (see Settings).
        </SecondaryText>
      </Box>
    </Box>
  );
}

export default OralB;
